/**
 * Thin fetch client for the QMD Functions API.
 * Cached in sessionStorage so a freshly-loaded Result page doesn't re-hit the
 * network when the user just generated the demo themselves.
 */
const BASE = '/api';

function cacheKey(shortId) { return `qmd:${shortId}`; }

export function readCachedDemo(shortId) {
  try {
    const raw = sessionStorage.getItem(cacheKey(shortId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCachedDemo(config) {
  try { sessionStorage.setItem(cacheKey(config.shortId), JSON.stringify(config)); } catch {}
}

export async function createDemo(config) {
  try {
    const r = await fetch(`${BASE}/demo`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (r.ok) {
      const saved = await r.json();
      writeCachedDemo(saved);
      return saved;
    }
    if (r.status < 500) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `create failed (${r.status})`);
    }
  } catch (e) {
    if (e && e.message && e.message.startsWith('create failed')) throw e;
  }
  // Backend unavailable — generate shortId client-side, persist to sessionStorage only.
  const { nanoid } = await import('nanoid');
  const saved = { ...config, shortId: nanoid(6), createdAt: new Date().toISOString(), _localOnly: true };
  writeCachedDemo(saved);
  return saved;
}

export async function fetchDemo(shortId) {
  const cached = readCachedDemo(shortId);
  if (cached) return cached;
  try {
    const r = await fetch(`${BASE}/demo/${encodeURIComponent(shortId)}`);
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`fetch failed (${r.status})`);
    const saved = await r.json();
    writeCachedDemo(saved);
    return saved;
  } catch {
    return null; // no backend + no cache → treat as missing
  }
}

export async function fetchRoads(south, west, north, east) {
  const url = `${BASE}/overpass?south=${south}&west=${west}&north=${north}&east=${east}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

export async function geocode(q) {
  // Try the backend proxy first (production path).
  try {
    const r = await fetch(`${BASE}/geocode`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ q }),
    });
    if (r.ok) {
      const data = await r.json();
      return data.results || [];
    }
    // 5xx (no Functions runtime locally) → fall through to direct call.
    if (r.status < 500) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `geocode failed (${r.status})`);
    }
  } catch (e) {
    // Network error or 5xx — try direct Nominatim below.
    if (e && e.message && e.message.startsWith('geocode failed')) throw e;
  }
  return geocodeDirect(q);
}

async function geocodeDirect(q) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&addressdetails=1&q=${encodeURIComponent(q)}`;
  const r = await fetch(url, { headers: { 'Accept-Language': 'en' } });
  if (!r.ok) throw new Error(`geocode failed (${r.status})`);
  const data = await r.json();
  return (Array.isArray(data) ? data : [])
    .slice(0, 5)
    .map((row) => ({ lat: parseFloat(row.lat), lon: parseFloat(row.lon), label: row.display_name }))
    .filter((row) => Number.isFinite(row.lat) && Number.isFinite(row.lon));
}
